import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Plus, Loader2, ArrowUpRight, ArrowDownLeft, Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { clientApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { RecognitionDialog } from '@/components/client/RecognitionDialog';

// Founder UAT 2026-06 — full readable recognition records (not counters only):
// Recibidos / Enviados tabs, each record shows sender/recipient, type,
// category, message, date and visibility.

const VIS_CLS = {
  public: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  managers_only: 'bg-amber-100 text-amber-700 border-amber-200',
  private: 'bg-slate-100 text-slate-600 border-slate-200',
};

const TYPE_KEY = { peer: 'typePeer', manager: 'typeManager', company: 'typeCompany' };

export default function MeRecognitionsCard() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const { user } = useApp();
  const [data, setData] = useState({ items: [], received_total: 0, sent_total: 0 });
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [tab, setTab] = useState('received');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await clientApi.myRecognitions('all', 50);
      setData(d || { items: [], received_total: 0, sent_total: 0 });
    } catch {
      setData({ items: [], received_total: 0, sent_total: 0 });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const received = data.items.filter((r) => r.to_user_id === user?.id);
  const sent = data.items.filter((r) => r.from_user_id === user?.id);
  const shown = tab === 'received' ? received : sent;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(isES ? 'es' : 'en', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return iso; }
  };

  return (
    <Card className="p-6 shadow-card" data-testid="me-recognitions-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-warning/15 text-warning shrink-0">
            <Award className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-semibold text-base">
              {isES ? 'Mis reconocimientos' : 'My recognitions'}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isES
                ? `Recibidos: ${data.received_total} · Enviados: ${data.sent_total}`
                : `Received: ${data.received_total} · Sent: ${data.sent_total}`}
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setDlgOpen(true)} data-testid="me-give-recognition">
          <Plus className="h-3 w-3 mr-1" />
          {isES ? 'Reconocer' : 'Recognize'}
        </Button>
      </div>

      {/* Recibidos / Enviados tabs */}
      <div className="flex gap-2 mb-3" data-testid="me-recognitions-tabs">
        <button
          type="button"
          onClick={() => setTab('received')}
          data-testid="me-rec-tab-received"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
            tab === 'received'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-transparent text-muted-foreground border-border hover:bg-muted/60'
          }`}
        >
          <ArrowDownLeft className="h-3 w-3" />
          {isES ? 'Recibidos' : 'Received'} ({data.received_total})
        </button>
        <button
          type="button"
          onClick={() => setTab('sent')}
          data-testid="me-rec-tab-sent"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
            tab === 'sent'
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-transparent text-muted-foreground border-border hover:bg-muted/60'
          }`}
        >
          <ArrowUpRight className="h-3 w-3" />
          {isES ? 'Enviados' : 'Sent'} ({data.sent_total})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center text-sm text-muted-foreground py-3" data-testid="me-recognitions-loading">
          <Loader2 className="h-3 w-3 mr-2 animate-spin" /> {isES ? 'Cargando…' : 'Loading…'}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="me-recognitions-empty">
          {tab === 'received'
            ? (isES
              ? 'Aún no has recibido reconocimientos. Empieza dando uno a un colega.'
              : 'No recognitions received yet — kick things off by recognizing a colleague.')
            : (isES
              ? 'Aún no has enviado reconocimientos.'
              : "You haven't sent any recognitions yet.")}
        </p>
      ) : (
        <ul className="space-y-3" data-testid="me-recognitions-list">
          {shown.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-border/70 p-3.5 bg-gradient-to-br from-amber-50/60 to-transparent"
              data-testid={`me-recognition-${r.id}`}
            >
              <div className="flex items-start gap-2.5">
                <Award className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {tab === 'received' ? (
                      <span className="text-sm font-semibold" data-testid={`me-rec-sender-${r.id}`}>{r.from_user_name}</span>
                    ) : (
                      <span className="text-sm" data-testid={`me-rec-recipient-${r.id}`}>
                        {isES ? 'Para' : 'To'}: <b>{r.to_user_name}</b>
                      </span>
                    )}
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      {t(`recognitionDialog.categories.${r.category}`)}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {t(`recognitionDialog.${TYPE_KEY[r.type] || 'typePeer'}`)}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap" data-testid={`me-rec-message-${r.id}`}>
                    {r.message}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-xs text-muted-foreground tabular-nums" data-testid={`me-rec-date-${r.id}`}>
                      {fmtDate(r.created_at)}
                    </span>
                    <Badge variant="outline" className={`text-xs ${VIS_CLS[r.visibility] || VIS_CLS.public}`} data-testid={`me-rec-visibility-${r.id}`}>
                      <Eye className="h-2.5 w-2.5 mr-1" />
                      {r.visibility === 'public'
                        ? (isES ? 'Pública' : 'Public')
                        : r.visibility === 'managers_only'
                          ? (isES ? 'Solo managers' : 'Managers only')
                          : (isES ? 'Privada' : 'Private')}
                    </Badge>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <RecognitionDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        currentUser={user}
        onCreated={load}
      />
    </Card>
  );
}
